import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { Subject } from 'rxjs';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { IReportSeatsWon } from 'src/app/interfaces/IReportSeatsWon';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportCandidateLeadingInDistrict } from 'src/app/interfaces/IReportCandidateLeadingInDistrict';
import * as _ from 'lodash';
import { ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'hemicycle-graph',
  templateUrl: './hemicycle-graph.component.html',
  styleUrls: ['./hemicycle-graph.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HemicycleGraphComponent implements AfterViewInit, OnDestroy {

  @Input() hideHeader = false;

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public seatsWons: IReportSeatsWon[] = [];
  public hemicycleDots: string[] = [];
  public hemicycleTooltips: string[] = [];
  public isFinal = false;
  public candidateTitle = '';
  public candidates: IReportCandidateLeadingInDistrict[] = [];

  constructor(public electionService: ElectionService) {
    // this.initHemicycle();
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  private loadChart(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    this.isFinal = election?.haChange?.electionStatus === ElectionStatusEnum.final;
    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      this.seatsWons = election?.electionCount?.parties.map(party => {
        return {
          partyId: party.partyId,
          party: party.partyName,
          seatsThisElection: party.leadingDistrictSeatCount,
          seatsPreviousElection: party.winningPreviousSeatCount,
          change: party.seatChange,
          colourCode: party.colourCode,
          seatList: party.leadingDistricts
        };
      }) ?? [];
    }

    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      this.candidateTitle = 'Candidate';
      this.candidates = election?.electionCount?.districts.filter(x => x.leadingCandidate !== null).map(district => {
        return {
          district: district.districtName,
          candidateName: `${district.leadingCandidate?.candidateName ?? ''} ${district.leadingCandidate?.wasPreviousMember ? ' (M)' : ''}`,
          wasPreviousMember: district.leadingCandidate?.wasPreviousMember ?? false,
          partyName: `${district.leadingCandidate?.partyName ?? ''} (${district.leadingCandidate?.partyId ?? ''})`,
          colourCode: district.leadingCandidate?.colourCode ?? '',
          distributionVotePercent: district.leadingCandidate?.twoCPVotePercent ?? 0,
          divWid: '',
          divStyle: `width: ${Math.round(district.leadingCandidate?.twoCPVotePercent ?? 0)}px; fill: ${district.leadingCandidate?.colourCode}; stroke-width: 3; stroke: ${district.leadingCandidate?.colourCode}`
        };
      }) ?? [];
    }

    this.fillHemicycle();
  }

  private initHemicycle(): void {
    this.hemicycleDots = [];
    this.hemicycleTooltips = [];
    for (let n = 1; n <= 47; n++){
      this.hemicycleDots.push('#E8E8E8');
      this.hemicycleTooltips.push('Outcome of election not yet decided');
    }
  }

  private fillHemicycle(): void {
    this.initHemicycle();
    let libSeats = 0;
    let labSeats = 0;
    let indSeats = 47;
    this.seatsWons.sort((a, b) => b.seatsThisElection - a.seatsThisElection);
    this.seatsWons.forEach(s => {
      switch (s.party) {
        case 'Liberal Party':
          const lib = this.seatsWons.find(t => t.party.includes('Liberal'));
          const districtLib = s.seatList.split(", ");
          if (lib) {
            libSeats = lib.seatsThisElection;
          }
          if (libSeats > 0) {
            indSeats -= libSeats;
            for (let ncount = 47; ncount > 47 - libSeats; ncount--){
              const candidate = this.candidates.find( c => c.district == districtLib[47-ncount])?.candidateName.split(", ")!;
              this.hemicycleDots[ncount] = s.colourCode;
              this.hemicycleTooltips[ncount] = districtLib[47-ncount] + ', ' + candidate[1] + _.capitalize(candidate[0]) + ', ' + s.party;
            }
          }
          break;

        case 'Australian Labor Party':
          const lab = this.seatsWons.find(t => t.party.includes('Labor'));
          const districtLab = s.seatList.split(", ");
          if (lab) {
            labSeats = lab.seatsThisElection;
            if (labSeats > 0) {
              indSeats -= labSeats;
              for (let ncount = 1; ncount<labSeats+1; ncount++){
                const candidate = this.candidates.find( c => c.district == districtLab[ncount-1])?.candidateName.split(", ")!;;
                this.hemicycleDots[ncount] = s.colourCode;
                this.hemicycleTooltips[ncount] = districtLab[ncount-1] + ', ' + candidate[1] + _.capitalize(candidate[0]) + ', ' + s.party;
              }
            }
          }
          break;

        default:
          const seats = s.seatsThisElection;
          var district = s.seatList.split(", ");
          let partySeats = 0;
          let seatsLabelled = labSeats + 1;
          for (let n = seatsLabelled; n < (seatsLabelled + seats); n++){
            const candidate = this.candidates.find( c => c.district == district[partySeats])?.candidateName.split(", ")!;;
            this.hemicycleDots[n] = s.colourCode;
            this.hemicycleTooltips[n] = district[partySeats] + ', ' + candidate[1] + _.capitalize(candidate[0]) + ', ' + s.party;
            partySeats++;
            labSeats++;
          }
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
